// RoleManagement.tsx

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../Services/Store";
import './RoleManagement.css';
import AddRolePanel from "./AddRolePanel";
import { FaTrash, FaEdit } from 'react-icons/fa';
import Paginations from '../Customs/pagination/Paginations';
import Services from "../Services/WebService";
import NoDataAvailable from "../Customs/NoDataAvailable/NoDataAvailable";
import Swal from "sweetalert2";

const rolesPerPage = 10;

const RoleManagement = () => {
  const navigate = useNavigate();

  // --- RBAC Access ---
  const accessList = useSelector((state: RootState) => state.auth.access || []);
const moduleAccess = accessList.find(item => item.code === "rba_control");
const hasReadAccess = moduleAccess?.read === true;
const hasWriteAccess = moduleAccess?.write === true;
const hasUpdateAccess = moduleAccess?.update === true;
const hasDeleteAccess = moduleAccess?.delete === true;

  // --- Protect page on mount (in case of direct access) ---
  useEffect(() => {
    if (!hasReadAccess) {
      navigate("/shop", { replace: true });
    }
  }, [hasReadAccess, navigate]);

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [editingRoleDetails, setEditingRoleDetails] = useState<any>(null);
  const [roles, setRoles] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  // --- Fetch roles ---
  const fetchRoles = useCallback(() => {
    setIsLoading(true);
    setCurrentPage(1);
    Services.getAPI<any>({
      action: "/v1/door-management/rbac/roles",
    })
      .then((data) => {
        if (Array.isArray(data)) {
          setRoles(data);
        } else if (data && Array.isArray(data.data)) {
          setRoles(data.data);
        } else if (data && Array.isArray(data.roles)) {
          setRoles(data.roles);
        } else {
          setRoles([]);
        }
      })
      .catch(() => setRoles([]))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // --- Delete handler (protected by delete permission) ---
  const handleDeleteRole = async (roleId: number) => {
    if (!hasDeleteAccess) return;
    const confirm = await Swal.fire({
      title: "Delete Role",
      text: "Are you sure you want to delete this role?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "swirlConfirmDelete",
        cancelButton: "swirlCancelDelete",
      },
      heightAuto: false,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;
    setIsLoading(true);
    Services.deleteAPI({
      action: `/v1/door-management/rbac/roles/${roleId}`,
    })
      .then(() => {
        Swal.fire({
          icon: "success",
          title: "Role deleted!",
          timer: 1200,
          showConfirmButton: false,
        });
        fetchRoles();
      })
      .catch(() => setIsLoading(false));
  };

  // --- Pagination ---
  const paginatedRoles = roles.slice(
    (currentPage - 1) * rolesPerPage,
    currentPage * rolesPerPage
  );

  const handleRoleTabClick = () => {
    fetchRoles();
  };

  // --- Edit handler (protected by update permission) ---
  const handleEditRole = (role: any) => {
    if (!hasUpdateAccess) return;
    setIsFetchingDetails(true);
    Services.getAPI<any>({
      action: `/v1/door-management/rbac/roles/${role.role_id}/access`,
    })
      .then((resp) => {
        setEditingRole(role);
        setEditingRoleDetails(resp?.data || []);
        setShowAddPanel(true);
      })
      .catch(() => {
        Swal.fire("Failed to fetch role details!", "", "error");
        setEditingRole(null);
        setEditingRoleDetails(null);
      })
      .finally(() => setIsFetchingDetails(false));
  };

  // --- Add handler (protected by write permission) ---
  const handleAddRole = () => {
    if (!hasWriteAccess) return;
    setEditingRole(null);
    setEditingRoleDetails(null);
    setShowAddPanel(true);
  };

  const handleClosePanel = () => {
    setShowAddPanel(false);
    setEditingRole(null);
    setEditingRoleDetails(null);
  };

  return (
    <div className="role-container">
      {/* Tabs */}
      <div className="mgmt-tabs">
        <button className="mgmt-tab active" onClick={handleRoleTabClick}>
          <span className="mgmt-tab-icon">🗂️</span> Role Management
        </button>
        <button className="mgmt-tab" onClick={() => navigate("/access-management")}>
          <span className="mgmt-tab-icon">🔑</span> Access Management
        </button>
        <button className="mgmt-tab" onClick={() => navigate("/state-city")}>
          <span className="mgmt-tab-icon">📍</span> State & City
        </button>
      </div>

      <h2 className="panel-title access-title">Role Management</h2>
      <p className="panel-desc">
        Create and manage user roles. Edit roles as needed for your organization.
      </p>

      {/* Add Role Button (RBAC: write) */}
      {hasWriteAccess && (
        <div className="add-role-button-wrapper">
          <button
            className="add-role-button"
            onClick={handleAddRole}
          >
            ➕ Add New Role
          </button>
        </div>
      )}

      <div className="role-table-container">
        {isLoading ? (
          <div className="role-page-loader">
            <NoDataAvailable isShowLoader={true} />
          </div>
        ) : roles.length === 0 ? (
          <NoDataAvailable isShowNoDataAvailable={true} message="No roles to display." />
        ) : (
          <table className="role-table">
            <thead>
              <tr>
                <th style={{ textAlign: "center" }}>Role ID</th>
                <th style={{ textAlign: "center" }}>Role Name</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRoles.map((role, index) => (
                <tr key={role.role_id || index}>
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>{role.role_id}</td>
                  <td className="role_name" style={{ textAlign: "center", verticalAlign: "middle" }}>
                    <strong>{role.role_name}</strong>
                  </td>
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                    {/* Delete icon: RBAC delete */}
                    
{hasUpdateAccess && (
  <FaEdit className="edit-icon" onClick={() => handleEditRole(role)} />
)}

{hasDeleteAccess && (
  <FaTrash className="trash-icon" onClick={() => handleDeleteRole(role.role_id)} />
)}

                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && roles.length > 0 && (
        <Paginations
          totalItems={roles.length}
          itemsCountPerPage={rolesPerPage}
          changePage={setCurrentPage}
          changeCurrentPage={currentPage}
        />
      )}

      {/* Add/Edit Role Panel */}
      <AddRolePanel
        open={showAddPanel}
        onClose={handleClosePanel}
        onRoleAdded={fetchRoles}
        editingRole={editingRole}
        editingRoleDetails={editingRoleDetails}
        isFetchingDetails={isFetchingDetails}
      />
    </div>
  );
};

export default RoleManagement;
